import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { useDispatch, useSelector } from 'react-redux'
import { AttachFile, Send, Visibility } from '@material-ui/icons'

import { Spacings, Text } from '@styles'
import { DrawerBodyWrapper, Textarea, TOAST_TYPE } from '@base'
import { strings } from '@constants'
import { getApprovalMessages, sendApprovalMessage, uploadApprovalDocument } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleFileViewer } from '@data/state/action/root'

import { Title } from '../styles'
import ReasonCard from '../ListApprovalMetrics/ReasonCard'

const Layout = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	gap: ${Spacings.SPACING_3B};
`

const ChatThread = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;

	/* Hide scrollbar */
	&::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none;
	scrollbar-width: none;
`

const MessagesInner = styled.div`
	margin-top: auto;
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B};
`

const Bubble = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	align-items: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
`

const Sender = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.palette.text.darkblack};
`

const BubbleBody = styled.span`
	max-width: 85%;
	padding: ${Spacings.SPACING_2B};
	border-radius: ${Spacings.SPACING_1B};
	font-size: ${Text.LARGE};
	color: ${({ theme, $own }) => ($own ? theme.palette.text.white : theme.palette.text.darkblack)};
	background: ${({ theme, $own }) => ($own ? theme.palette.text.blue : theme.palette.background.searchgrey)};
`

const MessageInput = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	border: 2px solid ${({ theme }) => theme.palette.background.searchgrey};
	border-radius: ${Spacings.SPACING_1B};
	padding: ${Spacings.SPACING_2B};
	flex-shrink: 0;

	& > *:first-child {
		flex: 1;
		margin: 0;
		padding: 0;
	}

	label {
		display: none;
	}

	textarea {
		border: none;
		width: 100%;
		height: 3.75rem;
		min-height: 3.75rem;
		line-height: 1.25rem;
		padding: 0;
		margin: 0;
		resize: none;
		overflow-y: auto;
	}
`

const SendIcon = styled.div`
	display: inline-flex;
	flex-shrink: 0;
	cursor: pointer;
	color: ${({ theme }) => theme.palette.text.blue};
`

const FileActions = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-shrink: 0;
	gap: ${Spacings.SPACING_2B};
`

const FileActionIcon = styled.div`
	display: inline-flex;
	flex-shrink: 0;
	cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
	color: ${({ theme, $disabled }) => ($disabled ? theme.palette.text.disabled : theme.palette.text.blue)};
	pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`

const ReasonMessages = ({ row, reason, creditApprovalId: creditApprovalIdProp, title, card }) => {
	const dispatch = useDispatch()
	const userId = useSelector((state) => state.getIn(['user', 'user']))?.user_id
	const [messages, setMessages] = useState([])
	const [messageText, setMessageText] = useState('')
	const [file, setFile] = useState(null)
	const bottomRef = useRef(null)
	const fileInputRef = useRef(null)

	const creditApprovalId = reason?.key || creditApprovalIdProp

	const nameById = useMemo(() => {
		// enquiry rows nest their approvals, while a fulfillment row is itself the approval
		const approval =
			(row?.approvals || []).find((item) => item?.credit_approval_id === creditApprovalId) ||
			(row?.credit_approval_id === creditApprovalId ? row : null)
		const map = {}
		;(approval?.members || []).forEach((member) => {
			if (member?.user_details?.user_id && member.user_details.full_name) map[member.user_details.user_id] = member.user_details.full_name
		})
		if (approval?.requested_by?.user_id) map[approval.requested_by.user_id] = approval.requested_by.full_name
		return map
	}, [row, creditApprovalId])

	const loadMessages = useCallback(() => {
		if (!creditApprovalId) return
		dispatch(showLoader(true))
		getApprovalMessages(creditApprovalId)
			.then((response) => setMessages(response || []))
			.catch(() => setMessages([]))
			.finally(() => dispatch(showLoader(false)))
	}, [dispatch, creditApprovalId])

	useEffect(() => {
		loadMessages()
	}, [loadMessages])

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: 'end' })
	}, [messages])

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = (event) => {
		const selectedFile = event?.target?.files?.[0]
		if (!selectedFile) return
		const isValidType = selectedFile.type === 'application/pdf' || selectedFile.type?.startsWith('image/')
		if (!isValidType) {
			dispatch(showToast(true, strings('only_pdf_or_image_allowed'), { type: TOAST_TYPE.ERROR }))
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}
		dispatch(showLoader(true))
		uploadApprovalDocument(selectedFile, creditApprovalId)
			.then((response) => {
				setFile(response || selectedFile)
				dispatch(showToast(true, strings('document_uploaded_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => {
				dispatch(showToast(true, err?.response?.data?.message || strings('something_went_wrong'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
				if (fileInputRef.current) fileInputRef.current.value = ''
			})
	}

	const handleViewFile = () => {
		const files = []
			.concat(file || [])
			.map((doc) => doc?.url || doc?.file_url || doc)
			.filter(Boolean)
		if (!files.length) return
		dispatch(toggleFileViewer(true, { files }))
	}

	const handleKeyDown = (event) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault()
			handleSendMessage()
		}
	}

	const handleSendMessage = () => {
		const message = messageText?.trim()
		if (!message || !creditApprovalId) return
		dispatch(showLoader(true))
		sendApprovalMessage({ creditApprovalId, message })
			.then(() => {
				setMessageText('')
				loadMessages()
			})
			.catch((err) => {
				dispatch(showToast(true, err?.response?.data?.message || strings('something_went_wrong'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	return (
		<DrawerBodyWrapper>
			<Layout>
				{card || (reason ? <ReasonCard row={row} reason={reason} hideActions statusTopRight /> : title && <Title>{title}</Title>)}

				<ChatThread>
					<MessagesInner>
						{messages.map((msg, idx) => {
							const senderId = msg?.sent_by?.user_id || msg?.sent_by
							const isOwn = senderId === userId
							return (
								<Bubble key={idx} $own={isOwn}>
									{!isOwn && <Sender>{nameById[senderId] || msg?.sent_by?.full_name || senderId}</Sender>}
									<BubbleBody $own={isOwn}>{msg?.message}</BubbleBody>
								</Bubble>
							)
						})}
						<div ref={bottomRef} />
					</MessagesInner>
				</ChatThread>

				<MessageInput>
					<Textarea
						id={`approval-message-${creditApprovalId}`}
						rows={3}
						placeholder={strings('enter_message')}
						value={messageText}
						onChange={(value) => setMessageText(value)}
						onKeyDown={handleKeyDown}
					/>
					<FileActions>
						<FileActionIcon $disabled={!file} onClick={handleViewFile}>
							<Visibility />
						</FileActionIcon>
						<FileActionIcon onClick={handleUploadClick}>
							<AttachFile />
						</FileActionIcon>
						<input ref={fileInputRef} type='file' accept='application/pdf,image/*' hidden onChange={handleFileChange} />
					</FileActions>
					<SendIcon onClick={handleSendMessage}>
						<Send />
					</SendIcon>
				</MessageInput>
			</Layout>
		</DrawerBodyWrapper>
	)
}

export default ReasonMessages
